import React, { useEffect, useState } from "react";
import {
  EuiButton,
  EuiPage,
  EuiPageBody,
  EuiPageHeader,
  EuiPageHeaderSection,
  EuiTitle,
  EuiSpacer,
  EuiBadge,
  EuiHealth,
  EuiInMemoryTable,
  EuiBasicTableColumn,
  EuiEmptyPrompt,
} from "@elastic/eui";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { useOpsanioCan } from "../can";
import { useToast } from "src/notifications";
import { UpdateContact } from "./update";
import moment from "moment";
import { ContactFlyout } from "./flyout";
import { TableSkeleton } from "src/components/skeleton";
import { Catalog } from "anketa-core/types";

export enum LeadPriority {
  low = "low",
  medium = "medium",
  high = "high",
}

export enum LeadStatus {
  new = "new",
  in_progress = "in_progress",
  resolved = "resolved",
  closed = "closed",
}

export interface Contact {
  id: string;
  name: string;
  email: string;
  company: string;
  topic: string;
  message: string;
  priority: LeadPriority;
  status: LeadStatus;
  createdAt: string;
  updatedAt: string;
  questionnaireId?: string;
}

export const ContactPage = ({
  updateCatalog,
}: {
  updateCatalog: (c: Catalog) => void;
}) => {
  const { translate } = useAnketaI18n();
  const { addToast } = useToast();
  const { Get, Put } = useApi();
  const { keycloak } = useAnketaAuth();
  // Editing a contact request: opsanio/contact · update.
  const canEdit = useOpsanioCan()(AnketaResourcesEnum.opsanioContact, "update");
  const i18nPrefix = "anketa.contact";

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isUpdateFlyoutVisible, setIsUpdateFlyoutVisible] =
    useState<boolean>(false);
  const [isFlyoutVisible, setIsFlyoutVisible] = useState<boolean>(false);
  const [formData, setFormData] = useState<Partial<Contact>>({});

  const getContactRequests = async () => {
    setLoading(true);
    try {
      const { success, data, error } = await Get<Array<Contact>>({
        path: `v2/${AnketaResourcesEnum.autonubil}/contacts`,
        responseType: "json",
        token: keycloak.token,
      });

      if (success) {
        // An empty list comes back as 204 with data = null.
        setContacts(Array.isArray(data) ? data : []);
      } else {
        // The API client never throws on HTTP errors - report them here.
        throw new Error(error?.message);
      }
      // (A success toast on every load - with an untranslated key - was noise.)
    } catch (error) {
      console.error("Error fetching contacts:", error);
      addToast({
        title: translate(`${i18nPrefix}.error.title`, {
          defaultMessage: "Could not load contact requests",
        }),
        color: "danger",
        text: translate(`${i18nPrefix}.error.text`, {
          defaultMessage: "Please try again later.",
        }),
      });
    } finally {
      setLoading(false);
    }
  };

  const updateContact = async () => {
    if (!selectedContact) return;
    const id = selectedContact.id;

    // Optimistic: merge the edit into the list and close the flyout immediately,
    // snapshot for rollback. (The API client returns a response and never throws,
    // so the prior try/catch around `await Put` was dead for HTTP errors.)
    const prevContacts = contacts;
    setContacts((cs) =>
      cs.map((c) => (c.id === id ? { ...c, ...formData } : c))
    );
    setIsUpdateFlyoutVisible(false);

    try {
      const resp = await Put({
        path: `v2/${AnketaResourcesEnum.autonubil}/contact/${id}`,
        value: formData,
        responseType: "json",
        token: keycloak.token,
      });
      if (!resp.success) {
        throw new Error(resp.error?.message);
      }
      addToast({
        title: translate(`${i18nPrefix}.update.success.title`),
        color: "success",
        text: translate(`${i18nPrefix}.update.success.text`),
      });
    } catch (error) {
      // Roll back the optimistic edit on failure.
      setContacts(prevContacts);
      console.error("Error updating contact:", error);
      addToast({
        title: translate(`${i18nPrefix}.update.error.title`),
        color: "danger",
        text: translate(`${i18nPrefix}.update.error.text`),
      });
    }
  };

  const openFlyout = (contact: Contact) => {
    setSelectedContact(contact);
    setFormData(contact);
    setIsUpdateFlyoutVisible(true);
  };

  const closeUpdateFlyout = () => {
    setIsUpdateFlyoutVisible(false);
    setSelectedContact(null);
    setFormData({});
  };

  const closeFlyout = () => {
    setIsFlyoutVisible(false);
    setSelectedContact(null);
  };

  const getPriorityColor = (priority: LeadPriority) => {
    switch (priority) {
      case LeadPriority.high:
        return "danger";
      case LeadPriority.medium:
        return "warning";
      default:
        return "subdued";
    }
  };

  const getStatusColor = (status: LeadStatus) => {
    switch (status) {
      case LeadStatus.new:
        return "primary";
      case LeadStatus.in_progress:
        return "accent";
      case LeadStatus.resolved:
        return "success";
      case LeadStatus.closed:
        return "subdued";
      default:
        return "subdued";
    }
  };

  const columns: Array<EuiBasicTableColumn<Contact>> = [
    {
      field: "name",
      name: "Name",
      sortable: true,
    },
    {
      field: "email",
      name: "Email",
      sortable: true,
    },
    {
      field: "company",
      name: "Company",
      sortable: true,
    },
    {
      field: "topic",
      name: "Topic",
      truncateText: true,
    },
    {
      field: "priority",
      name: "Priority",
      render: (priority: LeadPriority) => (
        <EuiBadge color={getPriorityColor(priority)}>
          {priority.toUpperCase()}
        </EuiBadge>
      ),
    },
    {
      field: "status",
      name: "Status",
      render: (status: LeadStatus) => (
        <EuiHealth color={getStatusColor(status)}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </EuiHealth>
      ),
    },
    {
      field: "createdAt",
      name: "Created",
      render: (date: string) =>
        moment(new Date(date)).format("DD.MM.YYYY HH:mm"),
      sortable: true,
    },
    {
      field: "updatedAt",
      name: "Updated",
      render: (date: string) =>
        moment(new Date(date)).format("DD.MM.YYYY HH:mm"),
      sortable: true,
    },
    {
      actions: [
        {
          name: translate("anketa.leads.table.actions.show"),
          description: translate("anketa.leads.table.actions.show"),
          icon: "eye",
          type: "icon",
          onClick: (contact: Contact) => {
            setSelectedContact(contact);
            setIsFlyoutVisible(true);
          },
        },
        {
          name: translate("anketa.table.details"),
          description: translate("anketa.table.details"),
          icon: "pencil",
          type: "icon",
          available: () => canEdit,
          onClick: (contact: Contact) => {
            openFlyout(contact);
          },
        },
      ],
    },
  ];

  useEffect(() => {
    getContactRequests();
  }, []);

  return (
    <EuiPage>
      <EuiPageBody>
        <EuiPageHeader>
          <EuiPageHeaderSection>
            <EuiTitle size="l">
              <h1>Contact Requests</h1>
            </EuiTitle>
          </EuiPageHeaderSection>
          <EuiPageHeaderSection>
            <EuiButton onClick={getContactRequests} iconType="refresh">
              Refresh
            </EuiButton>
          </EuiPageHeaderSection>
        </EuiPageHeader>

        <EuiSpacer size="l" />

        {loading && contacts.length === 0 ? (
          <TableSkeleton rows={6} />
        ) : (
          <EuiInMemoryTable
            items={contacts}
            columns={columns}
            loading={loading}
            message={
              <EuiEmptyPrompt
                iconType="email"
                title={
                  <h3>
                    {translate("anketa.opsanio.contact.empty.title", {
                      defaultMessage: "No contact requests",
                    })}
                  </h3>
                }
                body={
                  <p>
                    {translate("anketa.opsanio.contact.empty.body", {
                      defaultMessage:
                        "There are no contact requests yet. New requests will appear here.",
                    })}
                  </p>
                }
                actions={
                  <EuiButton onClick={getContactRequests} iconType="refresh">
                    {translate("anketa.common.refresh", {
                      defaultMessage: "Refresh",
                    })}
                  </EuiButton>
                }
              />
            }
            pagination={{
              pageIndex: 0,
              pageSize: 10,
              pageSizeOptions: [10, 25, 50],
            }}
          />
        )}

        {isUpdateFlyoutVisible && selectedContact && (
          <UpdateContact
            closeFlyout={closeUpdateFlyout}
            formData={formData}
            loading={loading}
            selectedContact={selectedContact}
            setFormData={setFormData}
            updateContact={updateContact}
          />
        )}
        {isFlyoutVisible && selectedContact && (
          <ContactFlyout
            onClose={closeFlyout}
            onEdit={
              canEdit
                ? (contact) => {
                    setIsFlyoutVisible(false);
                    openFlyout(contact);
                  }
                : undefined
            }
            selectedContact={selectedContact}
          />
        )}
      </EuiPageBody>
    </EuiPage>
  );
};
